import React, { useState, useEffect, useCallback } from "react";
import { FileText, Search, Download, ChevronDown, ChevronUp, Filter, User, MapPin, Target, Clock, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_COLORS = {
  solide: "bg-green-100 text-green-700",
  "améliorable": "bg-yellow-100 text-yellow-700",
  fragile: "bg-orange-100 text-orange-700",
  critique: "bg-red-100 text-red-700",
};

export const DiagnosticsAdminSection = () => {
  const [diagnostics, setDiagnostics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [filters, setFilters] = useState({ metier: "", canton: "", level: "", timing: "" });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/diagnostic/admin/list`);
        setDiagnostics(res.data.diagnostics || []);
      } catch { /* */ }
      setLoading(false);
    })();
  }, []);

  const downloadPdf = useCallback(async (id, name) => {
    try {
      const res = await axios.get(`${API}/diagnostic/admin/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `diagnostic_${name || id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { /* */ }
  }, []);

  const filtered = diagnostics.filter((d) => {
    const a = d.answers || {};
    if (filters.metier && a.metier !== filters.metier) return false;
    if (filters.canton && a.canton !== filters.canton) return false;
    if (filters.level && d.level !== filters.level) return false;
    if (filters.timing && a.timing !== filters.timing) return false;
    return true;
  });

  const metiers = [...new Set(diagnostics.map((d) => d.answers?.metier).filter(Boolean))];
  const cantons = [...new Set(diagnostics.map((d) => d.answers?.canton).filter(Boolean))];

  if (loading) {
    return (
      <div className="bg-card rounded-lg border p-6">
        <h2 className="text-lg font-bold mb-4 flex items-center gap-2"><FileText className="w-5 h-5 text-primary" /> Diagnostics</h2>
        <div className="flex justify-center py-8"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border p-6" data-testid="diagnostics-admin">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-bold flex items-center gap-2"><FileText className="w-5 h-5 text-primary" /> Diagnostics ({filtered.length})</h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={async () => {
            try {
              const res = await axios.get(`${API}/diagnostic/admin/export`, { responseType: "blob" });
              const url = window.URL.createObjectURL(new Blob([res.data]));
              const a = document.createElement("a"); a.href = url; a.download = "diagnostics_export.csv"; a.click();
            } catch {}
          }}>
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}>
            <Filter className="w-4 h-4 mr-1" /> Filtres
          </Button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5 p-4 bg-muted/30 rounded-lg">
          <select value={filters.metier} onChange={(e) => setFilters((p) => ({ ...p, metier: e.target.value }))} className="border rounded px-3 py-2 text-sm">
            <option value="">Tous les métiers</option>
            {metiers.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <select value={filters.canton} onChange={(e) => setFilters((p) => ({ ...p, canton: e.target.value }))} className="border rounded px-3 py-2 text-sm">
            <option value="">Tous les cantons</option>
            {cantons.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={filters.level} onChange={(e) => setFilters((p) => ({ ...p, level: e.target.value }))} className="border rounded px-3 py-2 text-sm">
            <option value="">Tous les niveaux</option>
            <option value="critique">Critique</option>
            <option value="fragile">Fragile</option>
            <option value="améliorable">Améliorable</option>
            <option value="solide">Solide</option>
          </select>
          <select value={filters.timing} onChange={(e) => setFilters((p) => ({ ...p, timing: e.target.value }))} className="border rounded px-3 py-2 text-sm">
            <option value="">Toutes urgences</option>
            <option value="maintenant">Maintenant</option>
            <option value="30_jours">30 jours</option>
            <option value="3_mois">3 mois</option>
            <option value="plus_tard">Plus tard</option>
          </select>
        </div>
      )}

      {filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">Aucun diagnostic trouvé.</p>
      ) : (
        <div className="space-y-3">
          {filtered.map((d) => {
            const contact = d.contact || {};
            const name = `${contact.first_name || ""} ${contact.last_name || ""}`.trim() || "Anonyme";
            const isOpen = expanded === d.id;
            const scores = d.scores || {};
            const calcs = d.calculations || {};
            const a = d.answers || {};
            const lvlClass = LEVEL_COLORS[d.level] || "bg-slate-100 text-slate-600";

            return (
              <div key={d.id} className="border rounded-lg overflow-hidden">
                {/* Row */}
                <button onClick={() => setExpanded(isOpen ? null : d.id)} className="w-full flex items-center justify-between p-4 hover:bg-muted/20 transition-colors text-left">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xs font-black ${lvlClass}`}>
                      {scores.overall || 0}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-sm truncate">{name}</p>
                      <p className="text-xs text-muted-foreground">{a.metier} — {a.canton}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded ${lvlClass}`}>{d.level}</span>
                    {a.timing === "maintenant" && <Clock className="w-4 h-4 text-red-500" />}
                    {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </div>
                </button>

                {/* Detail */}
                {isOpen && (
                  <div className="border-t p-4 bg-muted/10 space-y-4">
                    {/* Contact */}
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div><span className="text-muted-foreground">Email :</span> {contact.email || "—"}</div>
                      <div><span className="text-muted-foreground">Tél :</span> {contact.phone || "—"}</div>
                      <div><span className="text-muted-foreground">Entreprise :</span> {contact.company || "—"}</div>
                      <div><span className="text-muted-foreground">Urgence :</span> {a.timing || "—"}</div>
                    </div>

                    {/* Scores */}
                    <div className="grid grid-cols-4 gap-2">
                      {["acquisition", "previsibilite", "qualification", "reactivite", "transformation", "suivi_devis", "digitalisation"].map((k) => (
                        <div key={k} className="text-center bg-white rounded p-2 border">
                          <p className="text-xs text-muted-foreground capitalize">{k.replace("_", " ")}</p>
                          <p className="font-bold text-sm">{scores[k] || 0}</p>
                        </div>
                      ))}
                    </div>

                    {/* Calcs */}
                    <div className="text-xs space-y-1">
                      <p>Clients : {calcs.clients_actuels || 0} → {calcs.clients_objectif || 0} (+{calcs.clients_manquants || 0})</p>
                      <p>CA potentiel : {(calcs.ca_potentiel_supplementaire || 0).toLocaleString("fr-CH")} CHF/mois</p>
                      <p>Taux transformation : {calcs.taux_transformation || 0}%</p>
                    </div>

                    {/* Priorities */}
                    {d.priorities?.length > 0 && (
                      <div className="text-xs">
                        <p className="font-semibold mb-1">Priorités :</p>
                        {d.priorities.map((p, i) => (
                          <p key={i} className="text-muted-foreground">#{i + 1} {p.title} ({p.severity})</p>
                        ))}
                      </div>
                    )}

                    {/* Setter summary */}
                    {d.setter_summary && (
                      <details className="text-xs">
                        <summary className="font-semibold cursor-pointer">Fiche setter/closer</summary>
                        <pre className="mt-2 bg-white p-3 rounded border text-xs whitespace-pre-wrap">{d.setter_summary}</pre>
                      </details>
                    )}

                    {/* PDF + Email */}
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => downloadPdf(d.id, contact.last_name)}>
                        <Download className="w-4 h-4 mr-1" /> PDF
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => window.open(`${API}/diagnostic/admin/${d.id}/email`, '_blank')}>
                        <Mail className="w-4 h-4 mr-1" /> Email
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
